import { AnalyticsEvents, useTranslation } from "@next-platform/client-common-core";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { useMedia } from "@next-platform/client-common-spicy-ui";
import { TrackingEventKey } from "@next-platform/packages-ts-types";
import { Button, Divider, Input, Spacer, Spinner } from "@nextui-org/react";
import React, { useCallback, useEffect, useRef } from "react";

import generateConversionEventId from "@/features/tracking/generateConversionEventId";
import fireConversion from "@/features/tracking/registry/fireConversion";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import { IPurchaseOptions } from "@/types";

import { Row, Text } from "../customComponents";
import Trans from "../Trans/Trans";
import OrderSummaryItems from "./OrderSummaryItems";

export type OrderSummaryProps = React.HTMLAttributes<HTMLDivElement> & {
  hideTitle?: boolean;
  discounts: {
    code: string;
    valid: boolean;
    error: string;
    total: string;
  }[];
  items: {
    name: string;
  }[];
  subtotal: number;
  total: number;
  tax?: {
    pct?: string;
    total?: string;
  } | null;
  allowCoupon?: boolean;
  billingPeriod?: BillingCycle;
  isCalculating?: boolean;
  currency?: string | null;
  updateCoupon?: (coupon: string) => void;
  currencySymbol: string;
  plan: string;
  isChangePlanLoading: boolean;
  purchaseOptions: IPurchaseOptions | null;
};

const COMPACT_SPACER_Y = 1.5;

// Hoisted to module scope so JSX attribute references are
// referentially stable across renders (react-perf/jsx-no-new-*).
const EMPTY_DISCOUNTS: OrderSummaryProps["discounts"] = [];
const EMPTY_ITEMS: OrderSummaryProps["items"] = [];
const COUPON_INPUT_CLASS_NAMES = {
  label: "text-default-700",
  inputWrapper: "bg-background",
} as const;

const preventFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
  event.preventDefault();
};

type Discount = OrderSummaryProps["discounts"][number];

// Hoisted to module scope so their inline predicates do not nest
// inside useCallback/forwardRef (max-nested-callbacks limit).
function findValidDiscount(discounts: Discount[]): Discount | undefined {
  return discounts.find((discount) => discount.valid);
}

function findInvalidDiscountForCode(discounts: Discount[], couponCode: string): Discount | undefined {
  return discounts.find((discount) => discount.code === couponCode && !discount.valid);
}

// Sums every discount `.total` in the array with no validity filter.
// The caller is responsible for filtering by `.valid` beforehand if it
// wants only valid discounts — this helper is a pure reducer.
function sumDiscountTotals(discounts: Discount[]): number {
  return discounts.reduce((runningTotal, discount) => runningTotal + parseFloat(discount.total), 0);
}

/* TODO(karl): [NX-7080] revisit this eslint-disable — split the OrderSummary JSX into mobile/desktop subcomponents so neither branch exceeds the line/complexity limits. */
/* eslint-disable max-lines-per-function, complexity -- This is the root OrderSummary view: the branching (mobile/desktop, coupon-allowed, tax/discount totals, calculating state) is inherent to the checkout UI surface. Splitting would fragment the JSX without clarifying the data flow. */
const OrderSummary = React.forwardRef<HTMLDivElement, OrderSummaryProps>(
  (
    {
      hideTitle = true,
      discounts = EMPTY_DISCOUNTS,
      subtotal,
      total,
      tax,
      items = EMPTY_ITEMS,
      allowCoupon,
      billingPeriod = BillingCycle.MONTHLY,
      updateCoupon,
      isCalculating,
      currency = "USD",
      currencySymbol,
      plan,
      isChangePlanLoading,
      purchaseOptions,
      ...props
    },
    ref,
  ) => {
    const { isMobile } = useMedia();
    const { t } = useTranslation();
    const [isCouponInputVisible, setIsCouponInputVisible] = React.useState(false);
    const [couponCode, setCouponCode] = React.useState("");
    const [couponError, setCouponError] = React.useState("");
    const { logEvent } = useQueueLogEvent();
    const refCheckoutStartEventTriggered = useRef(false);
    const refAppliedCoupon = useRef<string | null>(null);

    // Trackers are intentionally plain closures, not `useCallback`s.
    // `logEvent` from `useQueueLogEvent()` returns a fresh function
    // every render, so any `useCallback` wrapping these trackers would
    // produce a new identity every render anyway and force the effect
    // below to re-run on every render. Keeping them as closures +
    // narrow effect deps `[couponCode, discounts]` means the effect
    // fires only when the coupon/discounts actually change; the
    // `refCheckoutStartEventTriggered` / `refAppliedCoupon` guards
    // inside each tracker dedup the analytics events themselves.
    const trackSubCheckoutStart = (): void => {
      if ((isCalculating && !total) || isChangePlanLoading || !purchaseOptions || refCheckoutStartEventTriggered.current) {
        return;
      }
      logEvent(AnalyticsEvents.SUBSCRIPTION_CHECKOUT_START, { plan_id: plan, currency, plan_price: subtotal.toFixed(2) });
      // Browser-only intent event: no backend CAPI counterpart, so
      // `eventId` is a per-fire uuid (never need to dedup against a
      // server event). The same `refCheckoutStartEventTriggered` ref
      // that guards the analytics call also guards this — Meta sees
      // one `InitiateCheckout` per checkout-form mount.
      fireConversion({
        eventKey: TrackingEventKey.CheckoutStarted,
        eventId: generateConversionEventId(),
        value: subtotal,
        currency: currency ?? undefined,
      });
      refCheckoutStartEventTriggered.current = true;
    };

    const trackCouponApplied = (): void => {
      const couponIsApplied = findValidDiscount(discounts);
      if (couponIsApplied && refAppliedCoupon.current !== couponIsApplied.code) {
        logEvent(AnalyticsEvents.CHECKOUT_COUPON_APPLY, { coupon_code: couponCode, discount_amount: couponIsApplied.total });
        refAppliedCoupon.current = couponIsApplied.code;
      } else if (!couponIsApplied) {
        refAppliedCoupon.current = null;
      }
    };

    //we vallidate coupon from payload and set the error message
    useEffect(() => {
      if (couponCode) {
        const invalidDiscount = findInvalidDiscountForCode(discounts, couponCode);
        const couponErrorResponse = invalidDiscount ? invalidDiscount.error : "";
        setCouponError(couponErrorResponse);
        trackCouponApplied();
      }
      trackSubCheckoutStart();
      // TODO(karl): [NX-7080] revisit this eslint-disable — stabilize `logEvent` at the `useQueueLogEvent()` boundary so trackers can be listed here without firing on every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- trackers intentionally omitted: they close over `logEvent`, which `useQueueLogEvent()` returns as a fresh function every render. Listing them would fire this effect on every render; the `refCheckoutStartEventTriggered` / `refAppliedCoupon` guards inside the trackers dedup the analytics fires, so effect deps only need to track what semantically invalidates coupon validation: `couponCode` and `discounts`.
    }, [couponCode, discounts]);

    const handleShowCouponInput = useCallback(() => {
      setIsCouponInputVisible(true);
    }, []);

    const handleCouponChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      setCouponCode(event.target.value);
    }, []);

    const handleApplyCoupon = useCallback(() => {
      if (updateCoupon) {
        updateCoupon(couponCode);
      }
    }, [updateCoupon, couponCode]);

    const totalDiscount = sumDiscountTotals(discounts);

    const totalTaxes = parseFloat(tax?.total || "") || 0;

    const renderOrderSummaryPriceLabelKeyByBillingCycle = (billingCycle: BillingCycle): JSX.Element => {
      if (billingCycle === BillingCycle.MONTHLY) {
        return <Trans i18nKey="subscription:checkout.orderSummary.monthlyPrice" />;
      }
      if (billingCycle === BillingCycle.QUARTERLY) {
        return <Trans i18nKey="subscription:checkout.orderSummary.quarterlyPrice" />;
      }
      if (billingCycle === BillingCycle.ANNUAL) {
        return <Trans i18nKey="subscription:checkout.orderSummary.annualPrice" />;
      }
      return <Trans i18nKey="subscription:checkout.orderSummary.monthlyPrice" />;
    };

    if ((isCalculating && !total) || isChangePlanLoading || !purchaseOptions) {
      return (
        <div className="flex justify-center">
          <Spinner label={t("subscription:checkout.orderSummary.loading")} />
        </div>
      );
    }
    if (!total) {
      return null;
    }
    return (
      <div ref={ref} {...props}>
        {!hideTitle && (
          <>
            <h2 className="font-medium text-default-500">
              <Trans i18nKey="subscription:checkout.orderSummary.yourOrder" />
            </h2>
            <Divider className="mt-4" />
          </>
        )}
        <h3 className="sr-only">
          <Trans i18nKey="subscription:checkout.orderSummary.cartItems" />
        </h3>
        {isMobile ? (
          <Text variant={"regular"} type="font-[600]">
            <Trans i18nKey="subscription:checkout.orderSummary.checkoutSummary" />
          </Text>
        ) : null}
        <Row className={isMobile ? "justify-between items-center mt-4" : "justify-center"}>
          {isMobile ? (
            <dt className="text-small text-default-500">
              <Trans i18nKey="subscription:checkout.orderSummary.planName" />
            </dt>
          ) : null}
          {isMobile ? (
            <Text variant={"sm"} type="font-[600]">
              {plan}
            </Text>
          ) : (
            <OrderSummaryItems items={items} billingPeriod={billingPeriod} updateCoupon={updateCoupon} purchaseOptions={purchaseOptions} />
          )}
        </Row>
        <Spacer y={isMobile ? 2 : COMPACT_SPACER_Y} />
        <div>
          {allowCoupon && !isCouponInputVisible ? (
            <div className="text-xs text-right cursor-pointer" onClick={handleShowCouponInput}>
              <Trans i18nKey="subscription:checkout.orderSummary.applyCoupon" />
            </div>
          ) : null}
          {isMobile ? <Spacer y={2} /> : null}
          {allowCoupon && isCouponInputVisible ? (
            <form className="mb-4 mt-6 flex items-center gap-2" onSubmit={preventFormSubmit}>
              <Input
                classNames={COUPON_INPUT_CLASS_NAMES}
                color="primary"
                label={t("subscription:checkout.orderSummary.couponCode")}
                labelPlacement="inside"
                placeholder={t("subscription:checkout.orderSummary.enterCouponCode")}
                isInvalid={couponError !== ""}
                errorMessage={couponError ? t("subscription:checkout.orderSummary.invalidCoupon") : ""}
                variant="bordered"
                value={couponCode}
                onChange={handleCouponChange}
              />
              <Button className="cursor-pointer" type="submit" onClick={handleApplyCoupon} isLoading={isCalculating} aria-label="apply">
                <Trans i18nKey="subscription:checkout.orderSummary.apply" />
              </Button>
            </form>
          ) : null}
          <dl className={"flex flex-col gap-4 " + (isMobile ? "pb-1" : "py-4")}>
            <div className="flex justify-between">
              <dt className="text-small text-default-500">{renderOrderSummaryPriceLabelKeyByBillingCycle(billingPeriod)}</dt>
              <dd className="text-small font-semibold text-default-700">
                {currencySymbol}
                {subtotal.toFixed(2)}
              </dd>
            </div>

            {totalTaxes > 0 && (
              <div className="flex justify-between">
                <dt className="text-small text-default-500">
                  <Trans i18nKey="subscription:checkout.orderSummary.taxes" />
                </dt>
                <dd className="text-small font-semibold text-default-700">
                  {currencySymbol}
                  {totalTaxes.toFixed(2)}
                </dd>
              </div>
            )}
            {totalDiscount > 0 && (
              <div className="flex justify-between">
                <dt className="text-small text-default-500">
                  <Trans i18nKey="subscription:checkout.orderSummary.discount" />
                </dt>
                <dd className="text-small font-semibold text-success"> - {totalDiscount.toFixed(2)}</dd>
              </div>
            )}
            <Divider />
            <div className="flex justify-between">
              <dt className="text-small font-semibold text-default-500">
                <Trans i18nKey="subscription:checkout.orderSummary.youPay" />
              </dt>
              <dd className="text-small font-semibold text-default-700">
                {currencySymbol}
                {total.toFixed(2)}{" "}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    );
  },
);

OrderSummary.displayName = "OrderSummary";

export default OrderSummary;
